import { useCallback, useEffect, useRef, useState } from 'react';

interface UseListKeyboardNavOptions {
  /** Number of rows currently reachable with the keyboard. */
  itemCount: number;
  /** Called with the highlighted row index when the user presses Enter. */
  onSelect: (index: number) => void;
  /**
   * When this value changes the highlight snaps back to the first row. Pass the
   * current query / page / filter so a fresh result list always starts on top.
   */
  resetKey?: unknown;
  /** Disable navigation entirely (e.g. while the list is loading). */
  enabled?: boolean;
  /**
   * Optional predicate marking a row as unselectable (already in the cart, out
   * of stock, …). Disabled rows are skipped while arrowing and ignored on Enter.
   */
  isDisabled?: (index: number) => boolean;
}

/**
 * Shared Up/Down/Home/End + Enter keyboard navigation for custom option lists
 * (comboboxes, multi-selects and "search & add to cart" pickers) so every
 * dropdown in the dashboard behaves the same way.
 *
 * The list container must be attached to `listRef` and each row must spread
 * `data-nav-index` (via `getItemProps`) so the highlighted row can be scrolled
 * into view.
 */
export function useListKeyboardNav({
  itemCount,
  onSelect,
  resetKey,
  enabled = true,
  isDisabled,
}: UseListKeyboardNavOptions) {
  // Keep the latest values in refs so the callbacks stay stable.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const isDisabledRef = useRef(isDisabled);
  isDisabledRef.current = isDisabled;

  const isRowDisabled = useCallback((index: number) => Boolean(isDisabledRef.current?.(index)), []);

  /** Index of the first selectable row, or -1 when every row is disabled/empty. */
  const firstSelectable = useCallback(
    (count: number) => {
      for (let i = 0; i < count; i += 1) {
        if (!isRowDisabled(i)) return i;
      }
      return count > 0 ? 0 : -1;
    },
    [isRowDisabled],
  );

  /** Move `delta` rows from `from`, skipping disabled rows; stay put at edges. */
  const step = useCallback(
    (from: number, delta: number) => {
      if (itemCount === 0) return -1;
      let next = from + delta;
      while (next >= 0 && next < itemCount && isRowDisabled(next)) next += delta;
      if (next < 0 || next >= itemCount) return from;
      return next;
    },
    [itemCount, isRowDisabled],
  );

  const [highlightedIndex, setHighlightedIndex] = useState(() => firstSelectable(itemCount));
  const listRef = useRef<HTMLDivElement>(null);

  // Reset to the first row whenever the underlying result set changes.
  useEffect(() => {
    setHighlightedIndex(firstSelectable(itemCount));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Keep the highlight valid if the list shrinks or a row becomes disabled.
  useEffect(() => {
    setHighlightedIndex((index) => {
      if (itemCount === 0) return -1;
      if (index < 0 || index >= itemCount || isRowDisabled(index)) {
        for (let i = index < 0 ? 0 : index; i < itemCount; i += 1) {
          if (!isRowDisabled(i)) return i;
        }
        return firstSelectable(itemCount);
      }
      return index;
    });
  }, [itemCount, isRowDisabled, firstSelectable]);

  // Keep the highlighted row visible while arrowing through the list.
  useEffect(() => {
    if (highlightedIndex < 0) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-nav-index="${highlightedIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!enabled || itemCount === 0) return;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex((index) => (index < 0 ? firstSelectable(itemCount) : step(index, 1)));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex((index) => {
            if (index <= 0) return firstSelectable(itemCount);
            return step(index, -1);
          });
          break;
        case 'Home':
          event.preventDefault();
          setHighlightedIndex(firstSelectable(itemCount));
          break;
        case 'End':
          event.preventDefault();
          setHighlightedIndex(() => {
            for (let i = itemCount - 1; i >= 0; i -= 1) {
              if (!isRowDisabled(i)) return i;
            }
            return itemCount - 1;
          });
          break;
        case 'Enter':
          event.preventDefault();
          if (highlightedIndex >= 0 && !isRowDisabled(highlightedIndex)) {
            onSelectRef.current(highlightedIndex);
          }
          break;
        default:
          break;
      }
    },
    [enabled, itemCount, highlightedIndex, step, firstSelectable, isRowDisabled],
  );

  const getItemProps = useCallback(
    (index: number) => ({
      'data-nav-index': index,
      onMouseEnter: () => setHighlightedIndex(index),
    }),
    [],
  );

  return { highlightedIndex, setHighlightedIndex, listRef, onKeyDown, getItemProps };
}
